import {queryOptions, type UseQueryOptions} from '@tanstack/react-query';
import {BILLING_QUERY_KEY_MAP} from '../api/billing/constant';
import {getBillingCreditRefundRefundableAmount} from '../api/billing/getBillingCreditRefundRefundableAmount';
import {getBillingDkpgBankCodes} from '../api/billing/getBillingDkpgBankCodes';
import {postBillingDkpgAuthBankAccount} from '../api/billing/postBillingDkpgAuthBankAccount';
import type {FetcherOptions} from '../axios/axiosBffFetch';
import {requestContext} from '../common/requestContext';
import {ApiStatusCodeVo} from '../common/shared/api/billing/enums/ApiStatusCodeVo.enum';
import {PagewebError} from '../errors/PagewebError.class';
import {PagewebErrorType} from '../errors/PagewebErrorType.enum';
import {createSimpleApiResponseError} from '../graphql/api/createSimpleApiResponseError';
import {createRefundableAmount} from '../graphql/factory/RefundableAmount';
import {
  GetBankCodeListQuery,
  GetRefundableAmountQuery,
  type GetRefundableAmountQueryVariables,
  ResultStatus,
  VerifyBankAccountQuery,
  type VerifyBankAccountQueryVariables,
} from '../graphql/generated/types';

/**
 * Billing 도메인 Query Factory
 *
 * Adapter layer를 제거하고 Factory에서 직접 데이터 변환을 수행
 */
export const billingFactory = {
  /**
   * 은행 코드 목록 조회
   *
   * Origin: useGetBankCodeListQuery
   *
   * ⚠️ Resolver 로직:
   * - response가 없으면 에러 throw
   * - response.list 반환
   */
  bankCodeList: (
    variables?: {},
    options?: Omit<
      UseQueryOptions<GetBankCodeListQuery, PagewebError, GetBankCodeListQuery>,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey: BILLING_QUERY_KEY_MAP.bankCodeList(variables),
      queryFn: async () => {
        const response = await getBillingDkpgBankCodes(fetcherOptions);

        if (!response) {
          throw createSimpleApiResponseError('은행 코드를 찾을 수 없습니다.');
        }

        // Adapter의 combine 로직을 여기서 수행
        return {
          bankCodeList: response.list,
        };
      },
      ...options,
    }),

  /**
   * 계좌 인증
   *
   * Origin: useVerifyBankAccountQuery
   *
   * ⚠️ Resolver 로직:
   * - result가 SUCCESS면 ResultStatus.Success 반환
   * - 그 외에는 ResultStatus.Fail 반환
   */
  verifyBankAccount: (
    variables: VerifyBankAccountQueryVariables,
    options?: Omit<
      UseQueryOptions<
        VerifyBankAccountQuery,
        PagewebError,
        VerifyBankAccountQuery
      >,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey: BILLING_QUERY_KEY_MAP.verifyBankAccount(variables),
      queryFn: async () => {
        const response = await postBillingDkpgAuthBankAccount(
          {
            bank_account: variables.input.bankAccountNumber,
            bank_code: variables.input.bankCode,
            bank_owner: variables.input.bankAccountOwnerName,
          },
          fetcherOptions
        );

        // Adapter의 combine 로직을 여기서 수행
        return {
          authBankAccount: {
            status:
              response.result === 'SUCCESS'
                ? ResultStatus.Success
                : ResultStatus.Fail,
          },
        };
      },
      ...options,
    }),

  /**
   * 환불 가능 잔액 조회
   *
   * Origin: useGetRefundableAmountQuery
   *
   * ⚠️ Resolver 로직:
   * - requestContext에서 kakaoAccountId 사용
   * - response?.data가 없으면 에러 throw
   * - createRefundableAmount factory로 변환
   */
  refundableAmount: (
    variables?: GetRefundableAmountQueryVariables,
    options?: Omit<
      UseQueryOptions<
        GetRefundableAmountQuery,
        PagewebError,
        GetRefundableAmountQuery
      >,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey: BILLING_QUERY_KEY_MAP.refundableAmount(variables),
      queryFn: async () => {
        const {user} = requestContext.requestInfo;

        if (!user?.kakaoAccountId) {
          throw new Error('인증 정보가 없습니다.');
        }

        const response = await getBillingCreditRefundRefundableAmount(
          user.kakaoAccountId,
          fetcherOptions
        );

        if (response?.status === ApiStatusCodeVo.NeedUpdate) {
          throw new PagewebError({
            isSerializedPagewebError: true,
            type: PagewebErrorType.BillingApiStatusError,
            resultCode: response?.status,
            message: response?.message,
          });
        }

        if (!response?.data) {
          throw createSimpleApiResponseError(
            '환불 가능 잔액 정보를 가져오는데 실패했습니다.'
          );
        }

        // Adapter의 combine 로직을 여기서 수행
        return {
          refundableAmount: createRefundableAmount(response.data),
        };
      },
      ...options,
    }),
} as const;
